import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { usePWA } from '../contexts/PWAContext';
import { useI18n } from '../contexts/I18nContext';

const ua = navigator.userAgent;
const isAndroid = /Android/.test(ua);
const isAndroidChrome = isAndroid && /Chrome\//.test(ua) && !/EdgA|OPR|SamsungBrowser/.test(ua);
const isSamsungBrowser = /SamsungBrowser/.test(ua);

const InstallModal = () => {
  const { showModal, isIOS, deferredPrompt, handleInstall, dismissModal } = usePWA();
  const { t } = useI18n();

  // Determine which variant to show
  const showInstallBtn = !!deferredPrompt;
  const showIOSSteps = isIOS && !deferredPrompt;
  const showAndroidManual = isAndroidChrome && !deferredPrompt;
  const showOpenChrome = !showInstallBtn && !showIOSSteps && !showAndroidManual;

  return (
    <AnimatePresence>
      {showModal && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={dismissModal}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{ zIndex: 10001 }}
            data-testid="install-modal-backdrop"
          />

          {/* Bottom sheet */}
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 26, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 w-full max-w-md mx-auto bg-white rounded-t-3xl p-6 pb-10"
            style={{ zIndex: 10002 }}
            data-testid="install-modal"
          >
            {/* Drag handle */}
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />

            {/* Dismiss button */}
            <button
              onClick={dismissModal}
              className="absolute top-5 right-5 w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500"
              data-testid="install-modal-close"
            >
              <X size={16} />
            </button>

            {/* App icon */}
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-lg mb-4">
                <img src="/icons/icon-192.png" alt="iRemindit" className="w-full h-full object-cover" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('install.modal_title')}
              </h2>
              <p className="text-sm text-slate-500 max-w-xs">
                {t('install.modal_desc')}
              </p>
            </div>

            {/* Chrome / Edge — native install prompt */}
            {showInstallBtn && (
              <button
                onClick={handleInstall}
                data-testid="install-modal-install-btn"
                className="w-full py-3.5 bg-indigo-600 text-white rounded-2xl font-bold text-base mb-3 active:scale-[0.98] transition-transform shadow-sm"
              >
                {t('install.modal_install')}
              </button>
            )}

            {/* Android Chrome — manual install steps */}
            {showAndroidManual && (
              <div className="bg-slate-50 rounded-2xl p-4 mb-4 space-y-3">
                {[
                  { icon: '⋮', label: 'Tap the menu icon (3 dots) at the top right' },
                  { icon: '+', label: 'Tap "Add to Home screen"' },
                  { icon: '✓', label: 'Tap "Add" to install iRemindit' },
                ].map(({ icon, label }, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {icon}
                    </div>
                    <p className="text-sm text-slate-700">{label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* iOS Safari — step-by-step */}
            {showIOSSteps && (
              <div className="bg-slate-50 rounded-2xl p-4 mb-4 space-y-3">
                {[
                  { icon: '↑', label: t('settings.install_ios_step1') },
                  { icon: '+', label: t('settings.install_ios_step2') },
                  { icon: '✓', label: t('settings.install_ios_step3') },
                ].map(({ icon, label }, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {icon}
                    </div>
                    <p className="text-sm text-slate-700">{label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* True fallback — not Chrome/iOS */}
            {showOpenChrome && (
              <div className="bg-slate-50 rounded-2xl p-4 mb-4 text-center">
                <p className="text-sm text-slate-600">{t('install.open_chrome')}</p>
              </div>
            )}

            <button
              onClick={dismissModal}
              data-testid="install-modal-not-now"
              className="w-full py-3 text-slate-500 font-semibold text-sm"
            >
              {t('install.modal_not_now')}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default InstallModal;
